import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link2, XCircle, ExternalLink, RefreshCcw, Loader2, ChevronDown, ChevronRight } from "lucide-react";

const fmtEur = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(Number(n || 0));

/**
 * Panel embebido en OfertaForm que muestra las `merge_suggestions[]` de la oferta
 * y ofrece un botón "Recalcular" (POST /dedupe-one-external) para refrescarlas
 * sin esperar al barrido completo. También expone Vincular / Rechazar por sugerencia.
 *
 * Se oculta si la oferta ya está vinculada (external_id no vacío) o si es nueva.
 */
export default function OfertaMergeSuggestionsPanel({ oferta, onLinked }) {
  const [suggestions, setSuggestions] = useState(oferta?.merge_suggestions || []);
  const [collapsed, setCollapsed] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [externalsScanned, setExternalsScanned] = useState(null);

  // Recarga si la oferta cambia (por ejemplo al abrir un modal para otra oferta)
  useEffect(() => { setSuggestions(oferta?.merge_suggestions || []); }, [oferta?.id, oferta?.merge_suggestions]);

  const recalc = useCallback(async () => {
    if (!oferta?.id) return;
    setRecalculating(true);
    try {
      const { data } = await axios.post(`${API}/ofertas-comercial/${oferta.id}/dedupe-one-external`);
      setSuggestions(data.suggestions || []);
      setExternalsScanned(data.externals_scanned);
      if (!data.suggestions || data.suggestions.length === 0) {
        toast.info("Sin coincidencias", {
          description: `${data.externals_scanned?.toLocaleString("es-ES")} oportunidades PLACSP escaneadas · ningún match ≥ 70`,
        });
      } else {
        toast.success(`${data.suggestions.length} candidato(s) encontrado(s)`, {
          description: `Top score: ${Math.round(Math.max(...data.suggestions.map(s => s.score)))}`,
        });
      }
    } catch (e) {
      toast.error("Error recalculando", { description: e.response?.data?.detail || e.message });
    } finally {
      setRecalculating(false);
    }
  }, [oferta?.id]);

  const link = async (ext) => {
    const key = ext.external_id;
    setBusyKey(key);
    try {
      await axios.post(`${API}/ofertas-comercial/${oferta.id}/link-external`, { external_id: ext.external_id });
      toast.success("Vinculado", { description: `Oferta enlazada con ${ext.external_source}` });
      setSuggestions([]);
      onLinked?.();
    } catch (e) {
      toast.error("No se pudo vincular", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  };

  const reject = async (ext) => {
    const key = ext.external_id;
    setBusyKey(key);
    try {
      await axios.post(`${API}/ofertas-comercial/${oferta.id}/reject-external`, { external_id: ext.external_id });
      toast.success("Sugerencia rechazada permanentemente", { description: "No volverá a sugerirse salvo que cambie de forma material la evidencia (importe, organismo, fechas…)." });
      setSuggestions(prev => prev.filter(s => s.external_id !== ext.external_id));
    } catch (e) {
      toast.error("No se pudo rechazar", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  };

  // No renderizamos nada si la oferta ya está vinculada o si aún no existe (nueva)
  if (!oferta?.id) return null;
  if (oferta.external_id) return null;

  const has = suggestions.length > 0;

  return (
    <div className="col-span-3 border border-indigo-200 bg-indigo-50/50 rounded-lg p-3" data-testid="merge-panel">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-1.5 text-xs font-bold text-indigo-800"
          data-testid="merge-panel-toggle"
        >
          {collapsed ? <ChevronRight size={12}/> : <ChevronDown size={12}/>}
          <Link2 size={12}/>
          Vincular con anuncio oficial PLACSP
          {has && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-600 text-white font-bold ml-1">
              {suggestions.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={recalc}
          disabled={recalculating}
          data-testid="merge-panel-recalc"
          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-indigo-300 text-indigo-700 text-[11px] font-semibold hover:bg-indigo-100 disabled:opacity-50"
          title="Buscar oportunidades PLACSP que puedan coincidir con esta oferta (útil tras editar título / cliente / presupuesto)"
        >
          {recalculating ? <Loader2 size={10} className="animate-spin"/> : <RefreshCcw size={10}/>}
          {recalculating ? "Recalculando…" : "Recalcular"}
        </button>
      </div>

      {!collapsed && (
        <>
          {externalsScanned !== null && (
            <div className="text-[10px] text-indigo-600 mb-2">
              Último escaneo: {externalsScanned.toLocaleString("es-ES")} oportunidades PLACSP
            </div>
          )}
          {!has ? (
            <div className="text-[11px] text-gray-500 italic bg-white/60 rounded p-2">
              No hay candidatos pendientes de vincular con un anuncio oficial PLACSP. Pulsa <b>Recalcular</b> para buscar coincidencias frescas.
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="text-[10px] text-indigo-700 bg-white/70 rounded p-1.5">
                Vincular NO fusiona esta oferta con otra ni escribe en PLACSP: sólo asocia el anuncio oficial como su origen externo y rellena los campos que esta oferta aún tenía vacíos (plataforma, enlace, lectura IA). Lo que ya hayas editado manualmente no se toca.
              </div>
              {suggestions.map(s => {
                const busy = busyKey === s.external_id;
                const scoreColor =
                  s.score >= 85 ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                  s.score >= 75 ? "bg-amber-100 text-amber-800 border-amber-300" :
                  "bg-gray-100 text-gray-700 border-gray-300";
                return (
                  <div key={s.external_id} className="bg-white rounded border border-gray-200 p-2 flex gap-2 items-start" data-testid="merge-panel-item">
                    <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${scoreColor}`}>
                      {Math.round(s.score)}
                    </div>
                    <div className="flex-1 min-w-0 text-[11px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[9px] uppercase bg-indigo-100 text-indigo-800 px-1 py-0 rounded font-bold">
                          {s.external_source}
                        </span>
                        <span className="text-gray-500 font-mono text-[10px]">
                          {fmtEur(s.external_value_eur)}
                        </span>
                        {s.external_deadline && (
                          <span className="text-gray-500 text-[10px]">
                            Deadline {String(s.external_deadline).slice(0, 10)}
                          </span>
                        )}
                        {s.external_url && (
                          <a href={s.external_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-0.5 text-[10px] font-semibold">
                            <ExternalLink size={8}/> Abrir
                          </a>
                        )}
                      </div>
                      <div className="text-gray-800 mt-0.5 leading-snug font-medium">
                        {s.external_title}
                      </div>
                      {s.external_organism && (
                        <div className="text-gray-500 text-[10px] mt-0.5">
                          {s.external_organism}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        type="button"
                        data-testid={`merge-panel-link-${s.external_id.slice(-8)}`}
                        disabled={busy}
                        onClick={() => link(s)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {busy ? <Loader2 size={9} className="animate-spin"/> : <Link2 size={9}/>}
                        Vincular
                      </button>
                      <button
                        type="button"
                        data-testid={`merge-panel-reject-${s.external_id.slice(-8)}`}
                        disabled={busy}
                        onClick={() => reject(s)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-gray-300 text-gray-700 text-[10px] font-semibold hover:bg-gray-100 disabled:opacity-50"
                      >
                        <XCircle size={9}/> Rechazar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
